import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Instagram, Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { RevealLinesOnView } from "../Reveal";
import { BRAND, LOCATIONS, CONTACT_SERVICES, waLink } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", event_date: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Enquiry sent! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", service: "", event_date: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 bg-neutral-50 py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-6">
          <p className="overline mb-5 text-pp-red">Contact</p>
          <RevealLinesOnView lines={["LET'S FRAME", "YOUR MEMORIES."]} className="font-display text-5xl font-black leading-[0.9] tracking-tight text-pp-ink md:text-7xl" />
          <p className="mt-6 text-neutral-600">Bring us your favourite moments.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-14 md:grid-cols-12">
          {/* Form */}
          <div className="md:col-span-7">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border-l-2 border-pp-red bg-white p-10" data-testid="contact-success">
                <h3 className="font-display text-3xl font-bold text-pp-ink">Your enquiry is on its way.</h3>
                <p className="mt-4 text-neutral-600">We've received it and will reply soon. For a faster response, message us on WhatsApp.</p>
                <a href={waLink()} target="_blank" rel="noreferrer" data-testid="success-whatsapp-btn" className="pp-btn mt-8 inline-flex rounded-full bg-pp-red px-6 py-3 text-white hover:bg-pp-reddeep">
                  <MessageCircle size={16} /> Chat on WhatsApp
                </a>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                  <Field label="Name *" value={form.name} onChange={set("name")} testid="input-name" placeholder="Your name" />
                  <Field label="Phone" value={form.phone} onChange={set("phone")} testid="input-phone" placeholder="+91 ..." />
                  <Field label="Email *" type="email" value={form.email} onChange={set("email")} testid="input-email" placeholder="you@email.com" />
                  <Field label="Preferred Date" type="date" value={form.event_date} onChange={set("event_date")} testid="input-date" />
                </div>
                <div>
                  <label className="overline text-neutral-500">Service</label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {CONTACT_SERVICES.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, service: opt }))}
                        data-testid={`service-opt-${opt.split(" ")[0].toLowerCase()}`}
                        className={`pp-btn rounded-full border px-4 py-2 text-xs ${
                          form.service === opt ? "border-pp-red bg-pp-red text-white" : "border-neutral-300 text-neutral-600 hover:border-pp-red"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="overline text-neutral-500">Message *</label>
                  <textarea value={form.message} onChange={set("message")} data-testid="input-message" rows={4} placeholder="Tell us what you'd like to print, frame or gift..." className="mt-3 w-full border-0 border-b border-neutral-300 bg-transparent pb-3 font-body text-lg text-pp-ink outline-none transition-colors focus:border-pp-red" />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="pp-btn rounded-full bg-pp-red px-8 py-4 text-white hover:bg-pp-reddeep disabled:opacity-60">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                  {loading ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="md:col-span-4 md:col-start-9">
            <div className="bg-white p-6">
              <p className="overline text-pp-red">PHOTO PIXEL®</p>
              <p className="mt-2 text-sm text-neutral-500">Founder & Owner</p>
              <p className="font-display text-xl font-bold text-pp-ink">{BRAND.founder.name}</p>
              <div className="mt-6 space-y-4 border-t border-neutral-200 pt-6">
                {LOCATIONS.filter((l) => !l.comingSoon).map((l) => (
                  <div key={l.id} className="space-y-1">
                    <p className="overline text-neutral-400">{l.name} — {l.label}</p>
                    <a href={`tel:${l.tel}`} data-testid={`contact-phone-${l.id}`} className="flex items-center gap-2 text-pp-ink transition-colors hover:text-pp-red">
                      <Phone size={15} className="text-pp-red" /> {l.phone}
                    </a>
                    <a href={`mailto:${l.email}`} data-testid={`contact-email-${l.id}`} className="flex items-center gap-2 text-pp-ink transition-colors hover:text-pp-red">
                      <Mail size={15} className="text-pp-red" /> {l.email}
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-big" className="group mt-6 flex items-center justify-between bg-pp-red p-6 text-white transition-colors hover:bg-pp-reddeep">
              <div>
                <p className="overline text-white/80">Fastest reply</p>
                <p className="mt-1 font-display text-2xl font-bold">WhatsApp Us</p>
              </div>
              <MessageCircle size={34} className="transition-transform group-hover:scale-110" />
            </a>

            <div className="mt-6 flex gap-3">
              <a href={BRAND.social.productInsta} target="_blank" rel="noreferrer" data-testid="contact-instagram" title="@photopixel.co.in — Prints • Frames • Gifts" className="flex h-11 w-11 items-center justify-center border border-neutral-300 text-pp-ink transition-colors hover:border-pp-red hover:text-pp-red"><Instagram size={18} /></a>
              <a href={BRAND.social.storiesInsta} target="_blank" rel="noreferrer" data-testid="contact-instagram-stories" title="@photopixel_stories — Photography" className="flex h-11 items-center justify-center gap-2 border border-neutral-300 px-3 text-xs text-pp-ink transition-colors hover:border-pp-red hover:text-pp-red"><Instagram size={16} /> Stories</a>
              <a href={LOCATIONS[0].maps} target="_blank" rel="noreferrer" data-testid="contact-maps" className="flex h-11 w-11 items-center justify-center border border-neutral-300 text-pp-ink transition-colors hover:border-pp-red hover:text-pp-red"><MapPin size={18} /></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="overline text-neutral-500">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} className="mt-3 w-full border-0 border-b border-neutral-300 bg-transparent pb-3 font-body text-lg text-pp-ink outline-none transition-colors focus:border-pp-red" />
    </div>
  );
}
